'use client';

import React from 'react';
import { VStack } from '@terra/ui-styled-system';
import {
  DataMessageTypeEnum,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import { hasRenderableNode } from './chat-renderability';
import {
  renderHeadlineActivity,
  wrapChildNode,
  wrapNestedChildren,
} from './subagent-trace-rows';
import {
  buildConsolidatedSummary,
  buildSubAgentSummary,
  collectPartsFromRenderUnits,
  ensureBackgroundRunningHeadline,
  expandSubAgentChildUnits,
  getToolNameFromPart,
  isObjectResultPart,
  isPendingHITLPart,
  isRunningPart,
  isSubAgentGroupRunning,
  isSubAgentGroupStopped,
  partitionSubAgentChildParts,
  resolveSubAgentGroupExecution,
  splitSubAgentVisualTimeline,
  timelineWithParentHeadline,
  withRunningWorkTail,
  type RenderablePart,
  type SubAgentExecutionGroup,
  type ConsolidationSurface,
  type ChatRenderUnit,
} from './chat-message-list-grouping';
import { useBackgroundExecution } from './BackgroundExecutionContext';
import type { BuildCallbacksForItems } from '../hooks/useSiblingHITLCallbacks';
import { isCompletedTerminalResultPart } from './chat-message-part-accessors';
import type { MessageListCallbacks } from './ChatMessageList';
import {
  activityUsesOwnChrome,
  headlineKindForParentTool,
  surfaceForHeadlineKind,
} from './chat-headline-actions';
import { isFailedToolCallMessage } from './failed-tool-call-display';
import { getToolConfig } from './tool-config';

export type RenderRenderablePartArgs = {
  item: RenderablePart;
  nestedContent?: React.ReactNode;
  nestedSummary?: string;
  isNestedInSubAgent?: boolean;
  forceExpanded?: boolean;
  isGroupRunning?: boolean;
  callbacksOverride?: MessageListCallbacks;
};

export type RenderConsolidatedGroup = (
  items: RenderablePart[],
  options?: {
    isNestedInSubAgent?: boolean;
    callbacksOverride?: MessageListCallbacks;
    surface?: ConsolidationSurface;
  },
) => React.ReactNode;

function activityIsRunning({
  units,
  groupIsRunning,
  isLastActivity,
}: {
  units: ChatRenderUnit[];
  groupIsRunning: boolean;
  isLastActivity: boolean;
}): boolean {
  if (units.length === 0) {
    return groupIsRunning;
  }

  if (
    collectPartsFromRenderUnits(units).some((item) =>
      isRunningPart(item.processedPart),
    )
  ) {
    return true;
  }

  return isLastActivity && groupIsRunning;
}

function settleFinishedBackgroundChild(item: RenderablePart): RenderablePart {
  const dataMessage = item.processedPart.dataMessage;

  if (
    !dataMessage ||
    !isRunningPart(item.processedPart) ||
    !isToolCallDataMessage(dataMessage) ||
    dataMessage.metadata?.backgroundChild !== true
  ) {
    return item;
  }

  return {
    ...item,
    processedPart: {
      ...item.processedPart,
      dataMessage: { ...dataMessage, status: 'completed' },
    },
  };
}

function isToolCallDataMessage(
  dataMessage: NonNullable<RenderablePart['processedPart']['dataMessage']>,
): dataMessage is ToolCallGenericMessage {
  return (
    dataMessage.type === DataMessageTypeEnum.ToolCallStart ||
    dataMessage.type === DataMessageTypeEnum.ToolCallUpdate ||
    dataMessage.type === DataMessageTypeEnum.ToolCallComplete ||
    dataMessage.type === DataMessageTypeEnum.ToolCallError
  );
}

function hasFailedCreationPart(parts: RenderablePart[]): boolean {
  return parts.some(({ processedPart }) => {
    const dataMessage = processedPart.dataMessage;

    return (
      dataMessage !== undefined &&
      isToolCallDataMessage(dataMessage) &&
      isFailedToolCallMessage({
        status: dataMessage.status,
        type: dataMessage.type,
      }) &&
      getToolConfig(dataMessage.toolName).failedName !== undefined
    );
  });
}

function workHeadlineSummary(parts: RenderablePart[]): string | undefined {
  return buildSubAgentSummary(
    parts.filter(
      (item) =>
        !(
          item.processedPart.kind === 'assistant-text' &&
          item.processedPart.part.type === 'reasoning'
        ),
    ),
  );
}

export function renderSubAgentExecutionGroup({
  group,
  renderRenderablePart,
  renderConsolidatedGroup,
  buildCallbacksForItems,
  forceParentRunning = false,
  isStopped = false,
}: {
  group: SubAgentExecutionGroup;
  renderRenderablePart: (args: RenderRenderablePartArgs) => React.ReactNode;
  renderConsolidatedGroup: RenderConsolidatedGroup;
  buildCallbacksForItems: BuildCallbacksForItems;
  forceParentRunning?: boolean;
  isStopped?: boolean;
}): React.ReactNode {
  const groupCallbacks = buildCallbacksForItems({
    groupKey: group.parent.processedPart.key,
    items: group.children,
  });
  const parentKind = headlineKindForParentTool(
    getToolNameFromPart(group.parent.processedPart),
  );
  const groupIsStopped = isStopped || isSubAgentGroupStopped(group);
  const groupIsRunning =
    groupIsStopped || group.resultDelivered
      ? false
      : isSubAgentGroupRunning(group) || forceParentRunning;
  const displayGroup = groupIsRunning
    ? group
    : {
        ...group,
        children: group.children.map(settleFinishedBackgroundChild),
      };
  const nestedUnits = expandSubAgentChildUnits(
    partitionSubAgentChildParts(displayGroup.children),
  );
  const renderNestedUnit = (unit: ChatRenderUnit) => {
    if (unit.type === 'consolidated') {
      return {
        key: unit.key,
        rendered: renderConsolidatedGroup(unit.items, {
          isNestedInSubAgent: true,
          callbacksOverride: groupCallbacks,
          surface: unit.surface,
        }),
      };
    }

    if (unit.type === 'analyzer-group') {
      return {
        key: unit.key,
        rendered: renderRenderablePart({
          item: unit.parent,
          nestedContent: wrapNestedChildren(
            unit.children.length > 0
              ? [
                  wrapChildNode({
                    key: `${unit.key}-explored`,
                    rendered: renderConsolidatedGroup(unit.children, {
                      isNestedInSubAgent: true,
                      callbacksOverride: groupCallbacks,
                      surface: 'analyze-source',
                    }),
                  }),
                ]
              : [],
          ),
          nestedSummary: buildConsolidatedSummary(
            unit.children,
            'analyze-source',
          ),
          isNestedInSubAgent: true,
          forceExpanded: false,
          isGroupRunning:
            isRunningPart(unit.parent.processedPart) ||
            unit.children.some((item) => isRunningPart(item.processedPart)),
          callbacksOverride: groupCallbacks,
        }),
      };
    }

    return {
      key: unit.item.processedPart.key,
      rendered: renderRenderablePart({
        item: unit.item,
        isNestedInSubAgent: true,
        forceExpanded: isObjectResultPart(unit.item.processedPart),
        callbacksOverride: groupCallbacks,
      }),
    };
  };

  const flattenUnitToCards = (unit: ChatRenderUnit) => {
    if (unit.type === 'consolidated') {
      return unit.items.map((item) => ({
        key: item.processedPart.key,
        rendered: renderRenderablePart({
          item,
          isNestedInSubAgent: true,
          forceExpanded: isObjectResultPart(item.processedPart),
          callbacksOverride: groupCallbacks,
        }),
      }));
    }

    if (unit.type === 'analyzer-group') {
      return unit.children.map((item) => ({
        key: item.processedPart.key,
        rendered: renderRenderablePart({
          item,
          isNestedInSubAgent: true,
          forceExpanded: isObjectResultPart(item.processedPart),
          callbacksOverride: groupCallbacks,
        }),
      }));
    }

    return [renderNestedUnit(unit)];
  };

  const hasPendingHITL = displayGroup.children.some((item) =>
    isPendingHITLPart(item.processedPart),
  );
  const hasRunningContinuation = group.continuationParents.some((item) =>
    isRunningPart(item.processedPart),
  );
  const hasObjectResult = displayGroup.children.some((item) =>
    isObjectResultPart(item.processedPart),
  );
  const parentKey = group.parent.processedPart.key;
  const timeline = timelineWithParentHeadline({
    segments: ensureBackgroundRunningHeadline({
      segments: withRunningWorkTail({
        segments: splitSubAgentVisualTimeline({
          units: nestedUnits,
          parentKind,
          parentKey,
        }),
        isRunning: groupIsRunning,
        parentKind,
        parentKey,
      }),
      forceParentRunning: groupIsRunning,
      parentKind,
      parentKey,
    }),
    parentKind,
    parentKey,
    units: nestedUnits,
  });

  if (timeline.length === 0) {
    return null;
  }

  const lastActivityKey = [...timeline]
    .reverse()
    .find((segment) => segment.type === 'activity')?.key;

  const timelineNodes = timeline.flatMap((segment) => {
    const isLastActivity = segment.key === lastActivityKey;
    const isRunning = activityIsRunning({
      units: segment.units,
      groupIsRunning,
      isLastActivity,
    });
    const forceExpanded =
      isLastActivity &&
      (hasPendingHITL || hasObjectResult || hasRunningContinuation);
    const parts = collectPartsFromRenderUnits(segment.units);
    const ownSurface = surfaceForHeadlineKind(segment.kind);

    if (activityUsesOwnChrome(segment.kind) && ownSurface) {
      const rendered = renderConsolidatedGroup(parts, {
        isNestedInSubAgent: true,
        callbacksOverride: groupCallbacks,
        surface: ownSurface,
      });

      if (!hasRenderableNode(rendered) && segment.units.length > 0) {
        return [];
      }

      if (!hasRenderableNode(rendered)) {
        return [
          wrapChildNode({
            key: segment.key,
            rendered: renderHeadlineActivity({
              kind: segment.kind,
              isRunning,
              isStopped: groupIsStopped && segment.kind === parentKind,
              forceExpanded,
              nestedSummary: workHeadlineSummary(parts),
              expansionKey: segment.key,
            }),
          }),
        ];
      }

      return [wrapChildNode({ key: segment.key, rendered })];
    }

    if (segment.kind === 'passthrough') {
      return segment.units
        .flatMap(flattenUnitToCards)
        .filter(({ rendered }) => hasRenderableNode(rendered))
        .map(wrapChildNode);
    }

    const nestedContent = wrapNestedChildren(
      segment.units
        .flatMap(flattenUnitToCards)
        .filter(({ rendered }) => hasRenderableNode(rendered))
        .map(wrapChildNode),
    );

    return [
      wrapChildNode({
        key: segment.key,
        rendered: renderHeadlineActivity({
          kind: segment.kind,
          nestedContent,
          nestedSummary: workHeadlineSummary(parts),
          isRunning,
          isStopped: groupIsStopped && segment.kind === parentKind,
          hasFailedCreation: hasFailedCreationPart(parts),
          forceExpanded,
          defaultExpanded: parts.some((item) =>
            isCompletedTerminalResultPart(item.processedPart),
          ),
          expansionKey: segment.key,
        }),
      }),
    ];
  });

  return (
    <VStack
      key={parentKey}
      alignItems="stretch"
      gap="6px"
      width="100%"
      minW={0}
    >
      {timelineNodes}
    </VStack>
  );
}

export function SubAgentExecutionGroupView({
  group,
  renderRenderablePart,
  renderConsolidatedGroup,
  buildCallbacksForItems,
}: {
  group: SubAgentExecutionGroup;
  renderRenderablePart: (args: RenderRenderablePartArgs) => React.ReactNode;
  renderConsolidatedGroup: RenderConsolidatedGroup;
  buildCallbacksForItems: BuildCallbacksForItems;
}): React.ReactNode {
  const backgroundExecution = useBackgroundExecution();
  const { forceParentRunning, isStopped, resultDelivered } =
    resolveSubAgentGroupExecution({
      group,
      getBackgroundExecutionByToolCallId:
        backgroundExecution?.getBackgroundExecutionByToolCallId,
    });
  const groupForRender = resultDelivered
    ? { ...group, resultDelivered: true }
    : group;

  return renderSubAgentExecutionGroup({
    group: groupForRender,
    renderRenderablePart,
    renderConsolidatedGroup,
    buildCallbacksForItems,
    forceParentRunning,
    isStopped,
  });
}
